import type { TFormCustomField } from "components/TitleFormContainer/types";
import { termFieldsForMode } from "containers/CustomFieldsContainer/customFieldPayload";
import { formatLocaleDateString } from "helpers/utils";
import { t } from "i18next";
import type { ICustomField } from "store/customFieldsStore";

const SECTION_ORDER = ["parties", "channels", "settings"];

const isEmpty = (value: unknown) => value === null || value === undefined || value === "";

const formatRecapValue = (field: ICustomField, value: unknown) => {
    if (isEmpty(value)) return "-";
    if (field.valueType === "date" || value instanceof Date) {
        return (
            formatLocaleDateString({ date: value as string | Date, options: { numeric: true, timeZone: "UTC" } }) || "-"
        );
    }
    if (field.valueType === "boolean") {
        return Number(value) ? t("CustomFields.CollapseInfo.Yes") : t("CustomFields.CollapseInfo.No");
    }
    if (value !== null && typeof value === "object") {
        const name = "name" in value && typeof value.name === "string" ? value.name : "";
        return name || "-";
    }
    return String(value);
};

type SavedCustomField = { uuid?: string; customFieldTypeId?: number; value: unknown };

const bySection = (left: ICustomField, right: ICustomField) =>
    SECTION_ORDER.indexOf(left.formLocation) - SECTION_ORDER.indexOf(right.formLocation);

/** Values entered on the term form, in section order, ready for the recap. */
export const termFormCustomFieldEntries = (
    fieldTypes: ICustomField[],
    formValues: TFormCustomField[] | undefined,
    mode: "new" | "edit",
) => {
    const byUuid = new Map((formValues ?? []).map((item) => [item.uuid, item.value]));
    return termFieldsForMode(fieldTypes, mode)
        .slice()
        .sort(bySection)
        .map((field) => ({
            uuid: field.uuid,
            name: field.name,
            formLocation: field.formLocation,
            value: formatRecapValue(field, byUuid.get(field.uuid)),
        }));
};

/** Saved term values, matched by uuid so two fields can share a display name. */
export const termSavedCustomFieldEntries = (fieldTypes: ICustomField[], saved: SavedCustomField[] | undefined) => {
    if (!saved?.length) return [];
    const byUuid = new Map<string, unknown>();
    const byTypeId = new Map<number, unknown>();
    for (const item of saved) {
        if (item.uuid) byUuid.set(item.uuid, item.value);
        if (item.customFieldTypeId != null) byTypeId.set(item.customFieldTypeId, item.value);
    }

    return fieldTypes
        .filter((field) => field.relatedResource === "term" && field.valueType !== "longstring")
        .slice()
        .sort(bySection)
        .map((field) => ({
            uuid: field.uuid,
            name: field.name,
            formLocation: field.formLocation,
            value: formatRecapValue(field, byUuid.has(field.uuid) ? byUuid.get(field.uuid) : byTypeId.get(field.id)),
        }));
};
